import { defineStore } from 'pinia'
import { computed, ref, shallowRef } from 'vue'
import { getAuth } from 'firebase/auth'
import { doc } from 'firebase/firestore'
import { httpsCallable } from 'firebase/functions'
import { db, firebaseApp, functions } from '@/services/firebaseconfig'
import { onSnapshot } from '@/services/sessionListeners'
import { getTwilioDeviceClass } from '@/utils/twilioDevice'
import { playChatNotificationTone } from '@/utils/audioTones'
import { hasActiveCallElsewhere } from '@/services/activeCallTabCoordinator'
import { voiceCallService } from '@/services/VoiceCallService'
import { TeamVoiceClient } from '@/services/teamVoiceClient'
import { registerRefreshBlocker } from '@/services/backgroundRefresh'
import { useWalkieStore } from '@/stores/walkieStore'

const PROTOCOL = 'gse-native-team-voice-v1'
const roomOK = value => typeof value === 'string' && /^T[a-f0-9]{31}$/.test(value)
export const useTeamVoiceStore = defineStore('teamVoice', () => {
  const enabled = ref(false),
    pttEnabled = ref(false),
    current = shallowRef(null),
    review = shallowRef(null)
  const calls = shallowRef([]),
    loading = ref(false),
    message = ref(null)
  const walkie = useWalkieStore()
  let context = { uid: null, allowed: false, ptt: false, scopeKey: null },
    epoch = 0,
    reviewEpoch = 0,
    refreshing = false
  let signalUnsubscribe = null,
    inboxTimer = null,
    watching = false
  const heard = new Set()
  const identity = () => ({
    ...context,
    uid: getAuth(firebaseApp).currentUser?.uid || null,
    allowed: context.allowed && getAuth(firebaseApp).currentUser?.uid === context.uid,
  })
  const call = async (name, data) =>
    (await httpsCallable(functions, name, { timeout: 30000 })(data)).data
  const client = new TeamVoiceClient({
    call,
    getDevice: getTwilioDeviceClass,
    identity,
    externalCall: () =>
      !!voiceCallService.currentConnection || walkie.isInCall || hasActiveCallElsewhere(),
    onChange: value => {
      if (value && value.uid !== context.uid) return
      if (value?.error) message.value = value.error
      else if (value?.phase === 'connected') message.value = null
      current.value = value
    },
  })
  const busy = computed(() => current.value !== null || loading.value)
  registerRefreshBlocker('team-voice-call', () => client.session !== null)

  function visibilityChanged() {
    if (document.hidden) client.release()
    else refreshInbox()
  }
  function stopSignals() {
    signalUnsubscribe?.()
    signalUnsubscribe = null
    clearTimeout(inboxTimer)
    inboxTimer = null
    if (watching) {
      document.removeEventListener('visibilitychange', visibilityChanged)
      window.removeEventListener('blur', release)
      window.removeEventListener('pagehide', leave)
      window.removeEventListener('offline', leave)
      watching = false
    }
  }
  function configure(next) {
    const changed =
      context.uid !== next.uid ||
      context.scopeKey !== next.scopeKey ||
      context.allowed !== next.allowed ||
      context.ptt !== next.ptt
    if (!changed) {
      client.reconcile()
      return
    }
    client.end('access-changed')
    stopSignals()
    epoch++
    reviewEpoch++
    context = { ...next }
    enabled.value = next.allowed === true
    pttEnabled.value = next.ptt === true
    review.value = null
    calls.value = []
    current.value = null
    loading.value = false
    heard.clear()
    if (!enabled.value || !next.uid) return
    watching = true
    document.addEventListener('visibilitychange', visibilityChanged)
    window.addEventListener('blur', release)
    window.addEventListener('pagehide', leave)
    window.addEventListener('offline', leave)
    signalUnsubscribe = onSnapshot(
      doc(db, 'userPresence', next.uid),
      () => refreshInbox(),
      () => {}
    )
    refreshInbox()
  }
  async function refreshInbox() {
    if (!enabled.value || refreshing || !identity().allowed) return
    const selected = epoch,
      uid = context.uid
    refreshing = true
    clearTimeout(inboxTimer)
    try {
      const result = await call('getNativeStaffTeamVoiceInbox', {})
      if (epoch !== selected || !identity().allowed) return
      if (
        result?.uid !== uid ||
        result.nativeProtocol !== PROTOCOL ||
        !Array.isArray(result.calls) ||
        result.calls.length > 20
      )
        throw new Error('Invalid team inbox')
      calls.value = result.calls.filter(
        row =>
          row.uid === uid &&
          row.nativeProtocol === PROTOCOL &&
          roomOK(row.roomId) &&
          ['open', 'ptt'].includes(row.mode) &&
          (row.mode !== 'ptt' || pttEnabled.value) &&
          ['direct', 'group'].includes(row.kind) &&
          typeof row.conversationId === 'string' &&
          Date.parse(row.expiresAt) > Date.now()
      )
      for (const row of calls.value) {
        if (
          row.mode === 'open' &&
          row.ringing === true &&
          !heard.has(row.roomId) &&
          !current.value &&
          !walkie.isInCall &&
          !voiceCallService.currentConnection
        ) {
          heard.add(row.roomId)
          playChatNotificationTone()
        }
      }
    } catch (_) {
      if (epoch === selected) calls.value = []
    } finally {
      refreshing = false
      if (epoch === selected && enabled.value) inboxTimer = setTimeout(refreshInbox, 30000)
      else if (enabled.value) refreshInbox()
    }
  }
  async function open(target, mode = 'open') {
    if (!enabled.value || busy.value) return
    const selected = ++reviewEpoch
    message.value = null
    review.value = null
    loading.value = true
    try {
      const value = await client.scope(target, mode)
      if (selected === reviewEpoch && enabled.value) review.value = value
    } catch (_) {
      if (selected === reviewEpoch)
        message.value = 'This conversation could not be checked. Try the call again in a moment.'
    } finally {
      if (selected === reviewEpoch) loading.value = false
    }
  }
  async function join(row) {
    if (!enabled.value || busy.value || !roomOK(row?.roomId)) return
    const selected = ++reviewEpoch
    message.value = null
    loading.value = true
    try {
      const value = await call('getNativeStaffTeamVoiceStatus', {
        actorUid: context.uid,
        nativeProtocol: PROTOCOL,
        roomId: row.roomId,
      })
      if (selected !== reviewEpoch || !identity().allowed) return
      if (
        value?.uid !== context.uid ||
        value.nativeProtocol !== PROTOCOL ||
        value.roomId !== row.roomId ||
        value.kind !== row.kind ||
        value.mode !== row.mode ||
        value.phase === 'ended' ||
        !Array.isArray(value.people)
      )
        throw new Error('The call changed')
      const peer = value.people.filter(person => person.uid !== context.uid)
      if (row.kind === 'direct' && peer.length !== 1) throw new Error('The conversation changed')
      const scope = await client.scope(
        row.kind === 'group'
          ? { kind: 'group', groupId: value.conversationId }
          : { kind: 'direct', peerId: peer[0].uid },
        row.mode
      )
      if (selected !== reviewEpoch || !identity().allowed) return
      if (scope.current?.roomId !== row.roomId) throw new Error('This room changed')
      review.value = scope
    } catch (_) {
      // The invitation is stale (most often the caller's side already ended the room). Drop it
      // from the list now rather than at the next 30 s poll; no refresh control exists to ask for.
      if (selected === reviewEpoch) {
        message.value = 'This call has already ended or changed.'
        refreshInbox()
      }
    } finally {
      if (selected === reviewEpoch) loading.value = false
    }
  }
  function cancelReview() {
    reviewEpoch++
    review.value = null
    loading.value = false
  }
  async function confirm() {
    if (!review.value || !enabled.value || current.value) return
    const selected = review.value
    cancelReview()
    message.value = null
    try {
      await client.start(selected)
    } catch (_) {
      message.value = 'Finish the current audio session, then review this call again.'
    }
  }
  async function decline(row) {
    if (!enabled.value || busy.value || !roomOK(row?.roomId) || row.claimId) return
    try {
      await call('declineNativeStaffTeamVoice', {
        actorUid: context.uid,
        nativeProtocol: PROTOCOL,
        roomId: row.roomId,
      })
      await refreshInbox()
    } catch (_) {
      message.value = 'This invitation could not update. Try again in a moment.'
      refreshInbox()
    }
  }
  function leave() {
    client.end('left')
  }
  function release() {
    client.release()
  }
  function press() {
    return client.press()
  }
  function mute(value) {
    client.mute(value)
  }
  function retry() {
    return client.prepare().catch(() => {
      message.value = 'Your calling access could not be confirmed. Leave this call and try again.'
    })
  }
  function stop() {
    configure({ uid: null, allowed: false, ptt: false, scopeKey: null })
    message.value = null
  }
  return {
    enabled,
    pttEnabled,
    current,
    review,
    calls,
    loading,
    message,
    busy,
    configure,
    stop,
    refreshInbox,
    open,
    join,
    confirm,
    cancelReview,
    decline,
    leave,
    release,
    press,
    mute,
    retry,
  }
})
